/* eslint @typescript-eslint/explicit-function-return-type: off */

import React from 'react';
import PropTypes from 'prop-types';
import DataTableBeLike from '../molecules/DataTableBeLike';
import Project from '../../models/Project';
import AdPerformanceReportDataSource from '../../models/AdPerformanceReportDataSource';
import * as Resource from '../../apis/AdPerformanceReportImportJob';
import * as Routes from '../../routes/AdPerformanceReportImportJob';
import * as DataSourceRoutes from '../../routes/AdPerformanceReportDataSource';
import { formatDate, formatDateTime } from '../../lib/DateUtils';
import { formatInteger } from '../../lib/NumberUtils';

const scope = 'frontend.components.AdPerformanceReportImportJob.ListTable';

const propTypes = {
  project: PropTypes.exact(Project.propTypes),
  columns: PropTypes.arrayOf(PropTypes.string),
  fixedFilters: PropTypes.exact({
    dataSourceId: AdPerformanceReportDataSource.propTypes.id,
  }),
  totalPages: DataTableBeLike.propTypes.totalPages,
  currentPage: DataTableBeLike.propTypes.currentPage,
  limit: DataTableBeLike.propTypes.limit,
};

const defaultProps = {
  columns: [
    'createdAt',
    'dataSource',
    'sourceFile',
    'status',
    'dataCount',
    'validCount',
    'updatedAt',
    'actions',
  ],
  totalPages: DataTableBeLike.defaultProps.totalPages,
  currentPage: DataTableBeLike.defaultProps.currentPage,
  limit: DataTableBeLike.defaultProps.limit,
};

class ListTable extends React.Component {
  static propTypes = propTypes;
  static defaultProps = defaultProps;

  constructor(props) {
    super(props);

    this.tableColumns = this.tableColumns.bind(this);
    this.refreshList = this.refreshList.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleLimitChange = this.handleLimitChange.bind(this);

    const { totalPages, currentPage, limit } = props;
    this.state = {
      columns: this.tableColumns(),
      data: [],
      fetching: false,
      totalPages,
      currentPage,
      limit,
    };

    this.limitSelections = [
      limit,
      ...DataTableBeLike.defaultProps.limitSelections,
    ]
      .sort((a, b) => a - b)
      .reduce((selections, v) => {
        if (!v || selections.includes(v)) {
          return selections;
        }

        selections.push(v);
        return selections;
      }, []);
  }

  tableColumns() {
    const { columns } = this.props;
    return columns.reduce((tableColumns, column) => {
      switch (column) {
        case 'dataSource':
          tableColumns.push({
            header: I18n.t(`${scope}.header.dataSource`),
            data: ({ projectId, dataSource }) => (
              <a href={DataSourceRoutes.detailsPath(projectId, dataSource.id)}>
                {dataSource.name}
              </a>
            ),
          });
          break;
        case 'sourceFile':
          tableColumns.push({
            header: I18n.t(`${scope}.header.sourceFile`),
            data: (job) => (
              <a href={job.sourceFile.url}>{job.originalFilename}</a>
            ),
          });
          break;
        case 'sourceDate':
          tableColumns.push({
            header: I18n.t(`${scope}.header.sourceDate`),
            data: ({ sourceDate }) => formatDate(sourceDate),
          });
          break;
        case 'dataCount':
          tableColumns.push({
            header: I18n.t(`${scope}.header.dataCount`),
            data: ({ dataCount }) =>
              dataCount != null ? formatInteger(dataCount) : '-',
          });
          break;
        case 'validCount':
          tableColumns.push({
            header: I18n.t(`${scope}.header.validCount`),
            data: ({ dataCount, errorCount, excludedCount }) =>
              dataCount != null
                ? formatInteger(dataCount - errorCount - excludedCount)
                : '-',
          });
          break;
        case 'errorCount':
          tableColumns.push({
            header: I18n.t(`${scope}.header.errorCount`),
            data: ({ errorCount }) =>
              errorCount != null ? formatInteger(errorCount) : '-',
          });
          break;
        case 'excludedCount':
          tableColumns.push({
            header: I18n.t(`${scope}.header.excludedCount`),
            data: ({ excludedCount }) =>
              excludedCount != null ? formatInteger(excludedCount) : '-',
          });
          break;
        case 'status':
          tableColumns.push({
            header: I18n.t(`${scope}.header.status`),
            data: ({ status }) => I18n.t(`${scope}.datarow.status.${status}`),
          });
          break;
        case 'createdAt':
          tableColumns.push({
            header: I18n.t(`${scope}.header.createdAt`),
            data: ({ createdAt }) => formatDateTime(createdAt),
          });
          break;
        case 'updatedAt':
          tableColumns.push({
            header: I18n.t(`${scope}.header.updatedAt`),
            data: ({ updatedAt }) => formatDateTime(updatedAt),
          });
          break;
        case 'actions':
          tableColumns.push({
            header: I18n.t(`${scope}.header.actions`),
            data: (job) => (
              <a href={Routes.detailsPath(job.projectId, job.id)}>
                {I18n.t(`${scope}.datarow.actions.details`)}
              </a>
            ),
          });
          break;
      }
      return tableColumns;
    }, []);
  }

  componentDidMount() {
    this.refreshList();
  }

  refreshList(stateChanges = {}) {
    const newState = { ...this.state, ...stateChanges, fetching: true };
    this.setState(newState);

    const { project, fixedFilters } = this.props;
    const { currentPage: page, limit } = newState;
    Resource.list(project.id, {
      ...fixedFilters,
      page,
      limit,
    })
      .then((response) => {
        const { data, paging } = response;
        const { total: totalPages, current: currentPage } = paging;
        this.setState({
          data,
          totalPages,
          currentPage,
          fetching: false,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  handlePageChange(page) {
    this.refreshList({ currentPage: page });
  }

  handleLimitChange(limit) {
    this.refreshList({ limit, currentPage: 1 });
  }

  render() {
    const { columns, data, fetching, totalPages, currentPage, limit } =
      this.state;

    return (
      <DataTableBeLike
        columns={columns}
        data={data}
        loading={fetching}
        totalPages={totalPages}
        currentPage={currentPage}
        limitSelections={this.limitSelections}
        limit={limit}
        onPageChange={this.handlePageChange}
        onLimitChange={this.handleLimitChange}
        emptyDataMessage={I18n.t(`${scope}.message.emptyData`)}
      />
    );
  }
}

export default ListTable;
